import React from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

interface FinancialTableProps {
  data: {
    units: string;
    daoNights: string;
    memberNights: string;
    pricePerNight: string;
    creditsSold: string;
    grossPotential: string;
    creditPrice: string;
    occupancy40: string;
    totalRaised: string;
    reserveYield: string;
    purchasePrice: string;
    totalCoverage: string;
    legalAdmin: string;
    estimatedOps: string;
    reserveFund: string;
    surplus: string;
  };
  showCalculationExplanation?: boolean;
  onInfoClick?: () => void;
}

const FinancialTable: React.FC<FinancialTableProps> = ({ data, showCalculationExplanation = false, onInfoClick }) => {
  return (
    <div style={{ 
      backgroundColor: '#1f2937', 
      padding: '16px', 
      borderRadius: '8px', 
      border: '1px solid #4b5563',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      marginBottom: '20px',
      position: 'relative'
    }}>
      {onInfoClick && (
        <button 
          onClick={onInfoClick}
          className="absolute top-2 right-2 text-blue-300 hover:text-white transition-colors"
          aria-label="Show calculator key"
        >
          <InformationCircleIcon className="h-7 w-7" />
        </button>
      )}
      <div className="overflow-x-auto text-xs">
        <div className="grid grid-cols-2 gap-x-4 pb-1 border-b-2 border-gray-500">
          <div className="text-left py-2 px-2 font-semibold text-blue-300">Property Details</div>
          <div className="text-left py-2 px-2 font-semibold text-blue-300">Annual DAO Income</div>
        </div>
        <div className="divide-y divide-gray-700 text-gray-300">
          <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
            <div><span className="text-gray-500">Units:</span> <span className="font-semibold">{data.units}</span></div>
            <div><span className="text-gray-500">DAO Nights:</span> <span className="font-semibold">{data.daoNights}</span></div>
          </div>
          <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
            <div><span className="text-gray-500">Nights for Members:</span> <span className="font-semibold">{data.memberNights}</span></div>
            <div><span className="text-gray-500">Price per Night:</span> <span className="font-semibold">{data.pricePerNight}</span></div>
          </div>
          <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
            <div><span className="text-gray-500">Credits Sold:</span> <span className="font-semibold">{data.creditsSold}</span></div>
            <div><span className="text-gray-500">Gross Potential:</span> <span className="font-semibold">{data.grossPotential}</span></div>
          </div>
          <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
            <div><span className="text-gray-500">Credit Price:</span> <span className="font-semibold">{data.creditPrice}</span></div>
            <div><span className="text-gray-500">40% Occupancy:</span> <span className="font-semibold">{data.occupancy40}</span></div>
          </div>
          <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
            <div><span className="text-gray-500">Total Raised:</span> <span className="font-semibold">{data.totalRaised}</span></div>
            <div><span className="text-gray-500">Reserve Yield (5%):</span> <span className="font-semibold">{data.reserveYield}</span></div>
          </div>
          <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
            <div><span className="text-gray-500">Purchase Price:</span> <span className="font-semibold">{data.purchasePrice}</span></div>
            <div><span className="text-gray-500">Total Coverage:</span> <span className="font-bold">{data.totalCoverage}</span></div>
          </div>
          <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
            <div><span className="text-gray-500">Legal/Admin:</span> <span className="font-semibold">{data.legalAdmin}</span></div>
            <div><span className="text-gray-500">Estimated Ops Cost:</span> <span className="font-semibold">{data.estimatedOps}</span></div>
          </div>
          <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
            <div><span className="text-gray-500">Reserve Fund:</span> <span className="font-semibold">{data.reserveFund}</span></div>
            <div><span className="text-gray-500">Surplus:</span> <span className="text-green-400 font-bold">{data.surplus}</span></div>
          </div>
        </div>
      </div>
      {showCalculationExplanation && (
        <details className="mt-4">
          <summary className="text-blue-300 cursor-pointer hover:text-blue-200 text-sm">
            How the numbers are calculated
          </summary>
          <div style={{ 
            backgroundColor: '#111827', 
            padding: '16px', 
            borderRadius: '6px', 
            border: '1px solid #374151',
            marginTop: '8px'
          }}>
            <p className="text-gray-400 text-sm mb-4">
              The calculator automatically determines the required "credits per night" to make each project financially sustainable. It begins at a baseline value and increases incrementally until the total raised covers all initial costs—property price, legal/admin fees, and a minimum reserve—while also generating a positive projected surplus. Surplus is based on DAO income (40% of gross potential at the given rate), reserve yield, and estimated annual operating costs.

Operating costs default to $20 per day but can be adjusted to reflect local conditions, property type, and amenities. Actual costs are often lower thanks to automated check-ins via Key-linked QR codes and efficiencies from AI-enabled systems.
            </p>
          </div>
        </details>
      )}
    </div>
  );
};

export default FinancialTable; 